/**
 * Seasonal campaign windows.
 *
 * The business has two genuinely different demand drivers, and the site's
 * messaging should follow whichever one is live rather than averaging them:
 *
 *  - SPRING/SUMMER: discretionary landscaping and new construction. Topsoil,
 *    bark, patios, garden beds. The customer is improving something.
 *  - FALL: repair and weather preparation. Rutted driveways, potholes, mud,
 *    standing water. The customer is preventing something, and there is a
 *    real deadline — compacting gravel needs dry ground, so the work has to
 *    happen BEFORE the rain sets in, not after the driveway fails.
 *
 * The fall window opens mid-August (roughly six weeks before the first
 * sustained rain, which is when the work can still be done properly) and
 * closes at the end of November, by which point the season's message is no
 * longer "prepare" but "repair".
 *
 * Deliberately date-driven rather than a manual flag: the campaign should not
 * depend on someone remembering to switch it on, and it must switch itself
 * off so summer visitors are never told to beat a rain that is months away.
 */

/** Fall driveway + drainage prep season: Aug 15 – Nov 30. */
export function isFallPrepSeason(date: Date = new Date()): boolean {
  const month = date.getMonth() // 0-11
  const day = date.getDate()
  if (month === 7) return day >= 15 // August 15 onward
  return month >= 8 && month <= 10 // September, October, November
}

/**
 * How urgent the fall message should be. The same campaign reads differently
 * in late August (plan ahead) than in late October (you are out of time), and
 * a single flat message wastes the strongest thing we can say.
 */
export type FallPhase = 'early' | 'peak' | 'late' | null

export function getFallPhase(date: Date = new Date()): FallPhase {
  if (!isFallPrepSeason(date)) return null
  const month = date.getMonth()
  if (month === 7) return 'early' // Aug 15-31: dry ground, best window
  if (month === 8) return 'peak' // September: last reliable dry stretch
  return 'late' // Oct-Nov: rain has started
}
